import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Input, Button, IconButton, Flex, useToast, Table, Thead, Tbody, Tr, Th, Td, FormControl, FormLabel
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, EmailIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';

const API_URL = '/api/v1/notification-emails/';

function NotificationEmails() {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingEmail, setEditingEmail] = useState('');
  const [testSendingId, setTestSendingId] = useState(null);
  const [testSendResult, setTestSendResult] = useState({});
  const toast = useToast();
  // Test send handler
  const handleTestSend = async (id, email) => {
    setTestSendingId(id);
    setTestSendResult({});
    try {
      const res = await fetch('/api/v1/notification-emails/test-send/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      setTestSendResult(r => ({ ...r, [id]: res.ok ? 'success' : 'error' }));
      toast({ title: res.ok ? 'Test email sent!' : (data.error || 'Failed to send'), status: res.ok ? 'success' : 'error', duration: 3000 });
    } catch (e) {
      setTestSendResult(r => ({ ...r, [id]: 'error' }));
      toast({ title: 'Failed to send', status: 'error', duration: 3000 });
    }
    setTestSendingId(null);
  };

  const token = localStorage.getItem('token');

  const fetchEmails = async () => {
    const res = await fetch(API_URL, {
      headers: { Authorization: `Token ${token}` },
    });
    if (res.ok) {
      setEmails(await res.json());
    }
  };

  useEffect(() => { fetchEmails(); }, []);

  const handleAdd = async () => {
    if (!newEmail) return;
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${token}`,
      },
      body: JSON.stringify({ email: newEmail }),
    });
    if (res.ok) {
      setNewEmail('');
      fetchEmails();
      toast({ title: 'Email added', status: 'success', duration: 2000 });
    } else {
      toast({ title: 'Failed to add email', status: 'error', duration: 2000 });
    }
  };

  const handleDelete = async (id) => {
    const res = await fetch(`${API_URL}${id}/`, {
      method: 'DELETE',
      headers: { Authorization: `Token ${token}` },
    });
    if (res.ok) {
      fetchEmails();
      toast({ title: 'Email deleted', status: 'info', duration: 2000 });
    }
  };

  const handleEdit = (id, email) => {
    setEditingId(id);
    setEditingEmail(email);
  };

  const handleUpdate = async () => {
    const res = await fetch(`${API_URL}${editingId}/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${token}`,
      },
      body: JSON.stringify({ email: editingEmail }),
    });
    if (res.ok) {
      setEditingId(null);
      setEditingEmail('');
      fetchEmails();
      toast({ title: 'Email updated', status: 'success', duration: 2000 });
    }
  };

  return (
    <Box maxW="2xl" mx="auto" p={4}>
      <Heading size="md" mb={6}>Notification Email Addresses</Heading>
      <Box mb={8} p={4} bg="gray.50" borderRadius="md" boxShadow="sm">
        <form onSubmit={e => { e.preventDefault(); handleAdd(); }}>
          <Flex align="flex-end" gap={4}>
            <FormControl flex={1}>
              <FormLabel htmlFor="new-email">Add Email Address</FormLabel>
              <Input
                id="new-email"
                placeholder="user@example.com"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                type="email"
                required
              />
            </FormControl>
            <Button colorScheme="teal" type="submit">Add</Button>
          </Flex>
        </form>
      </Box>
      <Table variant="simple" size="md" bg="white" borderRadius="md" boxShadow="sm">
        <Thead>
          <Tr>
            <Th>Email Address</Th>
            <Th textAlign="center">Actions</Th>
            <Th textAlign="center">Test Send</Th>
          </Tr>
        </Thead>
        <Tbody>
          {emails.map(email => (
            <Tr key={email.id}>
              <Td>
                {editingId === email.id ? (
                  <Flex align="center" gap={2}>
                    <Input
                      value={editingEmail}
                      onChange={e => setEditingEmail(e.target.value)}
                      size="sm"
                      type="email"
                      required
                    />
                    <IconButton
                      icon={<CheckIcon />}
                      size="sm"
                      colorScheme="teal"
                      onClick={handleUpdate}
                      aria-label="Save"
                      mr={1}
                    />
                    <IconButton
                      icon={<CloseIcon />}
                      size="sm"
                      onClick={() => setEditingId(null)}
                      aria-label="Cancel"
                    />
                  </Flex>
                ) : (
                  email.email
                )}
              </Td>
              <Td textAlign="center">
                <Flex gap={2} justify="center">
                  <IconButton
                    icon={<EditIcon />}
                    size="sm"
                    onClick={() => handleEdit(email.id, email.email)}
                    aria-label="Edit"
                  />
                  <IconButton
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    onClick={() => handleDelete(email.id)}
                    aria-label="Delete"
                  />
                </Flex>
              </Td>
              <Td textAlign="center">
                <Button
                  size="sm"
                  leftIcon={<EmailIcon />}
                  isLoading={testSendingId === email.id}
                  colorScheme={testSendResult[email.id] === 'success' ? 'green' : (testSendResult[email.id] === 'error' ? 'red' : 'blue')}
                  variant={testSendResult[email.id] ? 'outline' : 'solid'}
                  onClick={() => handleTestSend(email.id, email.email)}
                >
                  {testSendResult[email.id] === 'success' ? 'Sent!' : (testSendResult[email.id] === 'error' ? 'Failed' : 'Test Send')}
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

export default NotificationEmails;
